import React, { useEffect, useState } from 'react';
import { api } from '@/api/client';
import ConfirmChoiceCard from './ConfirmChoiceCard';
import ConfirmDestructiveDialog from './ConfirmDestructiveDialog';
import RemovalSummaryPanel from './RemovalSummaryPanel';
import type { DeleteImpact } from '@/types';
import { markDiagnosticsStale } from '@/utils/diagnosticsRefresh';

type Props = {
  entityType: 'subscription' | 'search' | 'user';
  entityId: string;
  onClose: () => void;
  onDone: (message: string) => void;
};

type SearchRemoveScope = 'search-only' | 'search-and-subscriptions';

type SubscriptionAction = 'stop' | 'remove' | 'remove-and-search';

type DeleteScope = 'definition' | 'definition_and_queued_jobs' | 'definition_and_all_history';

export default function ConfirmDeleteModal({ entityType, entityId, onClose, onDone }: Props) {
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchScope, setSearchScope] = useState<SearchRemoveScope | null>(null);
  const [subscriptionAction, setSubscriptionAction] = useState<SubscriptionAction>('stop');
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('definition');

  useEffect(() => {
    setLoading(true);
    setError('');
    setSearchScope(null);
    setSubscriptionAction('stop');
    setDeleteScope('definition');
    const load =
      entityType === 'subscription'
        ? api.deleteImpact.subscription(entityId)
          : entityType === 'search'
          ? api.deleteImpact.search(entityId)
          : api.deleteImpact.user(entityId);
    load
      .then((data) => {
        setImpact(data);
        if (entityType === 'search' && !data.requires_remove_subscriptions) {
          setSearchScope('search-only');
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  const subscriptionCount = Number(impact?.will_remove.subscriptions ?? 0);

  async function confirm() {
    if (!impact) return;
    setSubmitting(true);
    setError('');
    try {
      let result;
      if (entityType === 'subscription') {
        if (subscriptionAction === 'stop') {
          await api.subscriptions.update(entityId, { is_enabled: false });
          markDiagnosticsStale();
          onDone(`Subscription stopped — no new alerts for ${impact.label}`);
          onClose();
          return;
        }
        result = await api.subscriptions.delete(entityId, {
          also_delete_search: subscriptionAction === 'remove-and-search',
          delete_scope: deleteScope,
        });
        markDiagnosticsStale();
      } else if (entityType === 'search') {
        const removeSubscriptions = searchScope === 'search-and-subscriptions';
        result = await api.searches.delete(entityId, {
          remove_subscriptions: removeSubscriptions,
          delete_scope: deleteScope,
        });
        markDiagnosticsStale();
      } else {
        result = await api.users.delete(entityId, { remove_subscriptions: true });
      }
      const msg = [
        ...result.removed.map((r) => `Removed: ${r}`),
        ...result.kept.map((k) => `Kept: ${k}`),
      ].join('. ');
      onDone(msg || 'Done');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  }

  const searchConfirmDisabled =
    entityType === 'search' &&
    Boolean(impact?.requires_remove_subscriptions) &&
    searchScope !== 'search-and-subscriptions';

  const subscriptionConfirmLabel =
    subscriptionAction === 'stop'
      ? submitting
        ? 'Stopping…'
        : 'Stop subscription'
      : subscriptionAction === 'remove-and-search'
        ? submitting
          ? 'Removing…'
          : 'Remove subscription and delete search'
        : submitting
          ? 'Removing…'
          : 'Remove subscription';

  const confirmLabel =
    entityType === 'subscription'
      ? subscriptionConfirmLabel
      : submitting
        ? 'Removing…'
        : 'Confirm removal';

  const confirmButtonClass =
    entityType === 'subscription' && subscriptionAction === 'stop'
      ? 'btn btn-primary'
      : 'btn btn-danger';

  const linkScopeCount = Number(impact?.will_remove.subscription_link_scopes ?? 0);
  const deliveryCount = Number(impact?.will_keep.delivery_records ?? 0);
  const searchName =
    impact?.search_name ?? impact?.label.split('→')[1]?.trim() ?? 'this search';

  return (
    <ConfirmDestructiveDialog
      title="Confirm removal"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className={confirmButtonClass}
            disabled={submitting || loading || !impact || searchConfirmDisabled}
            onClick={confirm}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {loading && <p className="muted">Loading impact…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {impact && (
        <>
          <p className="modal-lead">
            You are about to remove: <strong>{impact.label}</strong>
          </p>
          {entityType === 'user' ? (
            <p className="muted modal-hint">
              Removes the user account and active subscriptions. Search definitions, delivery history, and scrape
              history are preserved for audit.
            </p>
          ) : (
            <p className="muted modal-hint">{impact.hint}</p>
          )}

          {entityType !== 'subscription' && (
            <RemovalSummaryPanel willRemove={impact.will_remove} willKeep={impact.will_keep} />
          )}

          {entityType === 'subscription' && (
            <fieldset className="confirm-choice-group">
              <legend className="confirm-choice-group__legend">Action</legend>
              <ConfirmChoiceCard
                name="subscription-action"
                value="stop"
                checked={subscriptionAction === 'stop'}
                onSelect={() => setSubscriptionAction('stop')}
                variant="safe"
                title="Stop subscription"
                description={
                  <ul className="confirm-choice-card__audit">
                    <li>Keeps subscription record (paused)</li>
                    <li>Keeps {linkScopeCount} link scope(s)</li>
                    <li>Keeps {deliveryCount} delivery record(s) in Activity</li>
                  </ul>
                }
              />
              <ConfirmChoiceCard
                name="subscription-action"
                value="remove"
                checked={subscriptionAction === 'remove'}
                onSelect={() => setSubscriptionAction('remove')}
                variant="destructive"
                title="Remove subscription"
                description={
                  <ul className="confirm-choice-card__audit">
                    <li>Removes 1 subscription and {linkScopeCount} link scope(s)</li>
                    <li>Keeps {deliveryCount} delivery record(s) in Activity</li>
                  </ul>
                }
              />
              <ConfirmChoiceCard
                name="subscription-action"
                value="remove-and-search"
                checked={subscriptionAction === 'remove-and-search'}
                onSelect={() => setSubscriptionAction('remove-and-search')}
                variant="destructive"
                disabled={!impact.can_also_delete_search}
                title="Remove subscription and delete search"
                description={
                  impact.can_also_delete_search ? (
                    <ul className="confirm-choice-card__audit">
                      <li>Removes subscription and search &quot;{searchName}&quot;</li>
                      <li>Keeps {deliveryCount} delivery record(s) in Activity</li>
                    </ul>
                  ) : (
                    <>
                      Not available — {impact.other_search_subscribers ?? 0} other subscription(s) still use
                      &quot;{searchName}&quot;.
                    </>
                  )
                }
              />
            </fieldset>
          )}

          {entityType === 'search' && (
            <fieldset className="confirm-choice-group">
              <legend className="confirm-choice-group__legend">Action</legend>
              <ConfirmChoiceCard
                name="search-remove-scope"
                value="search-only"
                checked={searchScope === 'search-only'}
                onSelect={() => setSearchScope('search-only')}
                variant="safe"
                disabled={Boolean(impact.requires_remove_subscriptions)}
                title="Remove search only"
                description={
                  impact.requires_remove_subscriptions
                    ? `Not available while ${subscriptionCount} subscription(s) exist.`
                    : 'Removes the search definition. No subscriptions are linked.'
                }
              />
              <ConfirmChoiceCard
                name="search-remove-scope"
                value="search-and-subscriptions"
                checked={searchScope === 'search-and-subscriptions'}
                onSelect={() => setSearchScope('search-and-subscriptions')}
                variant="destructive"
                disabled={!impact.requires_remove_subscriptions && subscriptionCount === 0}
                title="Remove search + subscriptions"
                description={
                  impact.requires_remove_subscriptions
                    ? `Removes the search and ${subscriptionCount} subscription(s).`
                    : 'Only needed when subscriptions are linked to this search.'
                }
              />
            </fieldset>
          )}

          {entityType === 'subscription' && subscriptionAction !== 'stop' && (
            <fieldset className="confirm-choice-group">
              <legend className="confirm-choice-group__legend">Related data</legend>
              <ConfirmChoiceCard
                name="delete-scope"
                value="definition"
                checked={deleteScope === 'definition'}
                onSelect={() => setDeleteScope('definition')}
                variant="safe"
                title="Delete definition only"
                description="Remove the subscription. Delivery history stays in Activity."
              />
              <ConfirmChoiceCard
                name="delete-scope"
                value="definition_and_queued_jobs"
                checked={deleteScope === 'definition_and_queued_jobs'}
                onSelect={() => setDeleteScope('definition_and_queued_jobs')}
                variant="destructive"
                title="Delete definition + queued jobs"
                description="Cancel queued scrape/send jobs, then remove the subscription."
              />
              <ConfirmChoiceCard
                name="delete-scope"
                value="definition_and_all_history"
                checked={deleteScope === 'definition_and_all_history'}
                onSelect={() => setDeleteScope('definition_and_all_history')}
                variant="destructive"
                title="Delete definition + all related history"
                description="Permanently removes jobs, deliveries, and execution records for this subscription."
              />
            </fieldset>
          )}

          {entityType === 'search' && searchScope === 'search-and-subscriptions' && (
            <fieldset className="confirm-choice-group">
              <legend className="confirm-choice-group__legend">Related data</legend>
              <ConfirmChoiceCard
                name="delete-scope-search"
                value="definition"
                checked={deleteScope === 'definition'}
                onSelect={() => setDeleteScope('definition')}
                variant="safe"
                title="Delete definition only"
                description="Remove search and subscriptions. Past deliveries stay in Activity."
              />
              <ConfirmChoiceCard
                name="delete-scope-search"
                value="definition_and_queued_jobs"
                checked={deleteScope === 'definition_and_queued_jobs'}
                onSelect={() => setDeleteScope('definition_and_queued_jobs')}
                variant="destructive"
                title="Delete definition + queued jobs"
                description="Cancel queued jobs first, then remove search and subscriptions."
              />
              <ConfirmChoiceCard
                name="delete-scope-search"
                value="definition_and_all_history"
                checked={deleteScope === 'definition_and_all_history'}
                onSelect={() => setDeleteScope('definition_and_all_history')}
                variant="destructive"
                title="Delete definition + all related history"
                description="Permanently removes jobs, deliveries, and execution records for this search."
              />
            </fieldset>
          )}

          {entityType === 'user' && impact.subscription_names && impact.subscription_names.length > 0 && (
            <div className="modal-options">
              <p className="option-note">
                Subscriptions to remove: {impact.subscription_names.join(', ')}
              </p>
              <p className="muted option-note">Search definitions stay if other users use them.</p>
            </div>
          )}
        </>
      )}
    </ConfirmDestructiveDialog>
  );
}
